import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";

const emptySlot = () => ({ id: "", start: "10:00", end: "12:00" });

export default function AdminTimeslotCheckout() {
  const [active, setActive] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/merchant/config/timeslot-checkout").then(({ data }) => {
      setActive(Boolean(data.active));
      const raw = data.timeslot_checkout || {};
      setCfg({
        slots_enabled: Boolean(raw.slots_enabled),
        slots: Array.isArray(raw.slots) && raw.slots.length ? raw.slots : [emptySlot()],
        prompt: raw.prompt || "Select when do you want us to deliver your order",
        store_offline: Boolean(raw.store_offline),
        available_at: raw.available_at || "",
      });
    }).catch((e) => toast.error(extractErr(e)));
  }, []);

  if (!cfg) return <div className="text-slate-500">Loading…</div>;
  if (!active) {
    return (
      <div className="max-w-2xl space-y-4">
        <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> App Store</Link>
        <Card className="p-8 text-center text-slate-600">Unlock <strong>Timeslot Checkout</strong> from the App Store to add delivery windows and pause the store.</Card>
      </div>
    );
  }

  const setSlot = (i, patch) => {
    const slots = cfg.slots.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    setCfg({ ...cfg, slots });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        slots_enabled: Boolean(cfg.slots_enabled),
        slots: (cfg.slots || []).map((s) => ({ id: s.id, start: s.start, end: s.end, label: s.label })),
        prompt: cfg.prompt,
        store_offline: Boolean(cfg.store_offline),
        available_at: cfg.available_at || "",
      };
      const { data } = await api.put("/merchant/config/timeslot-checkout", payload);
      setCfg({
        ...data.timeslot_checkout,
        slots: (data.timeslot_checkout?.slots || []).length ? data.timeslot_checkout.slots : [emptySlot()],
      });
      toast.success("Timeslot Checkout saved");
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-5">
      <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1" data-testid="ts-back"><ArrowLeft className="w-4 h-4" /> App Store</Link>
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Timeslot Checkout</h1>
        <p className="text-sm text-slate-500 mt-1">Ask shoppers to pick a delivery window, or take the store offline until a time you set.</p>
      </div>

      <Card className="p-6 space-y-5 border-slate-200/70" data-testid="ts-slots-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-semibold text-slate-900">Dynamic time slot</div>
            <p className="text-sm text-slate-500 mt-1">When this is on and you have saved windows, checkout requires a slot. Shoppers see: “{cfg.prompt}”.</p>
          </div>
          <Switch checked={Boolean(cfg.slots_enabled)} onCheckedChange={(v) => setCfg({ ...cfg, slots_enabled: v })} data-testid="ts-slots-enabled" />
        </div>
        <div>
          <Label htmlFor="ts-prompt">Checkout text</Label>
          <Input id="ts-prompt" className="mt-1" data-testid="ts-prompt" value={cfg.prompt || ""} onChange={(e) => setCfg({ ...cfg, prompt: e.target.value })} />
        </div>
        <div className="space-y-3">
          {(cfg.slots || []).map((slot, i) => (
            <div key={slot.id || i} className="flex flex-wrap items-end gap-2" data-testid={`ts-slot-${i}`}>
              <div className="flex-1 min-w-[7rem]">
                <Label>From</Label>
                <Input type="time" className="mt-1" data-testid={`ts-slot-start-${i}`} value={slot.start || ""} onChange={(e) => setSlot(i, { start: e.target.value })} />
              </div>
              <div className="flex-1 min-w-[7rem]">
                <Label>To</Label>
                <Input type="time" className="mt-1" data-testid={`ts-slot-end-${i}`} value={slot.end || ""} onChange={(e) => setSlot(i, { end: e.target.value })} />
              </div>
              <Button type="button" variant="ghost" className="text-rose-600" data-testid={`ts-slot-remove-${i}`} onClick={() => setCfg({ ...cfg, slots: cfg.slots.filter((_, idx) => idx !== i) })}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" data-testid="ts-slot-add" onClick={() => setCfg({ ...cfg, slots: [...(cfg.slots || []), emptySlot()] })}>
          <Plus className="w-4 h-4 mr-1" /> Add time slot
        </Button>
      </Card>

      <Card className="p-6 space-y-5 border-slate-200/70" data-testid="ts-offline-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-semibold text-slate-900">Store availability</div>
            <p className="text-sm text-slate-500 mt-1">When this is on, every product shows an offline badge and Add to cart, Buy now, and Checkout are disabled.</p>
          </div>
          <Switch checked={Boolean(cfg.store_offline)} onCheckedChange={(v) => setCfg({ ...cfg, store_offline: v })} data-testid="ts-store-offline" />
        </div>
        <div>
          <Label htmlFor="ts-available-at">Available after</Label>
          <Input
            id="ts-available-at"
            type="datetime-local"
            className="mt-1"
            data-testid="ts-available-at"
            value={cfg.available_at || ""}
            onChange={(e) => setCfg({ ...cfg, available_at: e.target.value })}
          />
          <p className="text-xs text-slate-500 mt-1">Shown on the product badge: Store is currently offline to accept any order and it will be available after this date and time.</p>
        </div>
      </Card>

      <Button type="button" onClick={save} disabled={saving} data-testid="ts-save" className="bg-slate-900 hover:bg-slate-800">
        <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
      </Button>
    </div>
  );
}
